import { Injectable } from "@angular/core";
import { ThemeService } from "./theme.service";
import { BehaviorSubject, distinctUntilChanged, filter, Observable, tap } from "rxjs";
import { doc, DocumentData, Firestore, getDoc } from "@angular/fire/firestore";
import { isElectron } from "src/app/shared/utils/platform.utils";

@Injectable({providedIn: 'root'})
export class FeatureService {
    private _isDesktop = false;
    private _sweepbucks = true;
    private _picks = true;
    private _xpReward = false;
    loyaltyPoints = false;
    tutorial = false;
    jackpotTicket = true;

    private _phantomEnabled: BehaviorSubject<boolean> = new BehaviorSubject(null);
    private _showSweepsbucksRingGames = false;
    private _facebookLoginEnabled: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _featuredTournamentConfigId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    get phantomEnabled$(): Observable<boolean> {
        return this._phantomEnabled.asObservable().pipe(filter(status => status !== null), tap(status => console.log(status)), distinctUntilChanged());
    }

    get showSweepsbucksRingGames(): boolean {
        return this._showSweepsbucksRingGames;
    }

    get facebookLoginEnabled$(): Observable<boolean> {
        return this._facebookLoginEnabled.asObservable().pipe(distinctUntilChanged());
    }

    get featuredTournamentConfigId$(): Observable<string | null> {
        return this._featuredTournamentConfigId.asObservable().pipe(distinctUntilChanged());
    }

    constructor(
        private themeService: ThemeService,
        private firestore: Firestore,
    ) {
        this.determineConfig();
        this.getFirebaseFeaturesConfig();
    }

    private determineConfig() {
        if (isElectron()) {
            this.setConfig("desktop");
        } else {
            this.setConfig("web");
        }
    }

    get isDesktop() {
        return this._isDesktop;
    }

    get sweepbucks() {
        return this._sweepbucks;
    }

    get picks() {
        return this._picks;
    }

    get xpReward() {
        return this._xpReward;
    }

    private setConfig(buildType: BuildType) {
        switch (buildType) {
            case "web":
                this._isDesktop = false;
                this._sweepbucks = false;
                this._picks = false;
                this.loyaltyPoints = false;
                this.tutorial = false;
                this.jackpotTicket = false;
                this.themeService.setTheme('black-gold-theme');
                break;
            case "desktop":
                this._isDesktop = true;
                this._sweepbucks = true;
                this._picks = true;
                this._xpReward = false;
                this.loyaltyPoints = false;
                this.tutorial = false;
                this.jackpotTicket = true;
                this.themeService.setTheme('black-gold-theme');
                break;
            case "desktop_live":
                this._isDesktop = true;
                this._sweepbucks = false;
                this._picks = true;
                this._xpReward = true;
                this.loyaltyPoints = false;
                this.tutorial = false;
                this.jackpotTicket = false;
                this.themeService.setTheme('black-gold-theme');
            break;
        }
    }

    private async getFirebaseFeaturesConfig(): Promise<void> {
        try {
            const featuresDocData = await this.getDocData('config', 'features');
            if (featuresDocData && featuresDocData.facebookLoginEnabled !== undefined) {
                this._facebookLoginEnabled.next(featuresDocData.facebookLoginEnabled);
            }
            if (featuresDocData && featuresDocData.showSweepsbucksRingGames !== undefined) {
                this._showSweepsbucksRingGames = featuresDocData.showSweepsbucksRingGames;
            }

            const tournamentSettingsDocData = await this.getDocData('config', 'tournament-settings');
            if (tournamentSettingsDocData && typeof tournamentSettingsDocData.featuredTournamentConfigId === 'string' && tournamentSettingsDocData.featuredTournamentConfigId.length > 0) {
                this._featuredTournamentConfigId.next(tournamentSettingsDocData.featuredTournamentConfigId);
            }

            const walletDocData = await this.getDocData('config', 'wallets');
            if (walletDocData && walletDocData.phantomEnabled !== undefined) {
                this._phantomEnabled.next(walletDocData.phantomEnabled);
            } else {
                this._phantomEnabled.next(false); // default;
            }
        } catch (error) {
            console.error('Firebase feature data cannot be loaded!', error);
        }
    }

    private async getDocData(path: string, ...pathSegments: string[]): Promise<DocumentData> {
        try {
            const docRef = doc(this.firestore, path, ...pathSegments);
            const docData = (await getDoc(docRef)).data()
            return docData;
        } catch (error) {
            console.error(`Firebase feature data cannot be loaded! Path: { ${path} } and pathSegments: { ${pathSegments} }`, error);
        }
    }
}

type BuildType = "web" | "desktop" | "desktop_live";
